import React, { useEffect, useState, useRef } from 'react';
import styled from 'styled-components';
import { useInView } from 'framer-motion';

const Container = styled.div`
  width: 100%;
  height: 450px;
  position: relative;
  margin: 4rem 0;

  @media (max-width: 768px) {
    height: 250px;
    overflow-x: auto;
    overflow-y: hidden;
    padding-bottom: 1rem;
  }
`;

const NodeElement = styled.div`
  position: absolute;
  background: white;
  border: ${props => props.isEndpoint ? '2px' : '1.5px'} solid var(--border-color);
  border-radius: var(--radius-md);
  padding: ${props => props.isEndpoint ? '1rem 1.5rem' : '0.85rem 1.1rem'};
  transform: translate(-50%, ${props => props.visible ? '-50%' : '-30%'});
  transform-origin: ${props => props.transformOrigin || 'center'};
  opacity: ${props => props.visible ? 1 : 0};
  transition: all ${props => props.duration || 0.2}s ease-out;
  transition-delay: ${props => props.delay}ms;
  z-index: 2;
  text-align: center;
  min-width: ${props => props.isEndpoint ? '100px' : '85px'};
  cursor: default;

  @media (max-width: 768px) {
    min-width: ${props => props.isEndpoint ? '70px' : '60px'};
    padding: ${props => props.isEndpoint ? '0.6rem 0.8rem' : '0.5rem 0.6rem'};
  }
`;

const NodeLabel = styled.div`
  font-size: ${props => props.isEndpoint ? '13px' : '12px'};
  font-weight: ${props => props.isEndpoint ? '600' : '500'};
  color: var(--text-secondary);
  white-space: nowrap;
  letter-spacing: -0.01em;

  @media (max-width: 768px) {
    font-size: ${props => props.isEndpoint ? '10px' : '9px'};
  }
`;

const NodeSublabel = styled.div`
  font-size: 10px;
  color: var(--text-muted);
  margin-top: 3px;
  white-space: nowrap;
  opacity: 0.7;

  @media (max-width: 768px) {
    font-size: 7px;
  }
`;

const SVGContainer = styled.svg`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
`;

const WebLine = styled.path`
  fill: none;
  stroke: var(--text-secondary);
  stroke-width: 1.2;
  stroke-linecap: round;
  stroke-linejoin: round;
  vector-effect: non-scaling-stroke;
  opacity: ${props => props.visible ? 0.7 : 0};
  transition: opacity ${props => props.duration || 0.2}s ease-out;
  transition-delay: ${props => props.delay}ms;
`;

const Arrow = styled.polygon`
  fill: var(--text-secondary);
  opacity: ${props => props.visible ? 0.7 : 0};
  transition: opacity ${props => props.duration || 0.2}s ease-out;
  transition-delay: ${props => props.delay + 50}ms;
  pointer-events: none;
`;


const FlowDiagram = ({ nodes, connections }) => {
  const [visible, setVisible] = useState(false);
  const containerRef = useRef(null);
  const isInView = useInView(containerRef, { 
    once: true, 
    margin: "-100px",
    amount: 0.3 
  });
  
  useEffect(() => {
    // Trigger animations when in view
    if (isInView) {
      const timer = setTimeout(() => setVisible(true), 100);
      return () => clearTimeout(timer);
    }
  }, [isInView]);
  
  // Helper to convert percentage positions to pixels
  const getPosition = (percentX, percentY) => {
    // These will be relative to container size
    return {
      left: `${percentX}%`,
      top: `${percentY}%`
    };
  };
  
  
  // Helper to calculate point on cubic bezier curve at parameter t
  const getCubicBezierPoint = (p0, p1, p2, p3, t) => {
    const t2 = t * t;
    const t3 = t2 * t;
    const mt = 1 - t;
    const mt2 = mt * mt;
    const mt3 = mt2 * mt;
    
    return {
      x: mt3 * p0.x + 3 * mt2 * t * p1.x + 3 * mt * t2 * p2.x + t3 * p3.x,
      y: mt3 * p0.y + 3 * mt2 * t * p1.y + 3 * mt * t2 * p2.y + t3 * p3.y
    };
  };
  
  // Helper to create consistent elegant paths between nodes
  const createPath = (from, to, curved = true) => {
    const x1 = from.x;
    const y1 = from.y;
    const x2 = to.x;
    const y2 = to.y;
    
    const dx = x2 - x1;
    const dy = y2 - y1;
    
    if (!curved) {
      return `M ${x1} ${y1} L ${x2} ${y2}`;
    }
    
    // Smoother, better-connected curves
    if (Math.abs(dy) < 5) {
      // Horizontal - very gentle curve for better connection
      return `M ${x1} ${y1} C ${x1 + dx * 0.5} ${y1}, ${x2 - dx * 0.5} ${y2}, ${x2} ${y2}`;
    } else if (Math.abs(dx) < 5) {
      // Vertical - straight with slight curve
      return `M ${x1} ${y1} C ${x1} ${y1 + dy * 0.3}, ${x2} ${y2 - dy * 0.3}, ${x2} ${y2}`;
    } else {
      // Diagonal - smooth S-curve that connects well
      const midX = x1 + dx * 0.5;
      const midY = y1 + dy * 0.5;
      
      // Create control points for smooth connection
      const cp1x = x1 + dx * 0.25;
      const cp1y = y1;
      const cp2x = x2 - dx * 0.25;
      const cp2y = y2;
      
      return `M ${x1} ${y1} C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${x2} ${y2}`;
    }
  };
  
  // Calculate path length for animation
  const getPathLength = (path) => {
    // Approximate length calculation
    const coords = path.match(/[\d.]+/g);
    if (coords && coords.length >= 4) {
      const x1 = parseFloat(coords[0]);
      const y1 = parseFloat(coords[1]);
      const x2 = parseFloat(coords[coords.length - 2]);
      const y2 = parseFloat(coords[coords.length - 1]);
      return Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2));
    }
    return 100;
  };
  
  // Gate System - Build animation phases
  const nodeTimings = {};
  const connectionTimings = {};
  
  // Phase timing constants
  const NODE_APPEAR_TIME = 67;   // Time for node to appear (ms) - 3x faster
  const LINE_DRAW_TIME = 133;    // Time for line to draw (ms) - 3x faster
  const PHASE_GAP = 33;           // Gap between phases (ms) - 3x faster
  
  // Build phases based on dependencies
  const phases = [];
  const processedNodes = new Set();
  const processedConnections = new Set();
  
  // Find starting nodes (no incoming connections)
  const incomingMap = {};
  connections.forEach(conn => {
    if (!incomingMap[conn.to]) incomingMap[conn.to] = [];
    incomingMap[conn.to].push(conn);
  });
  
  // Phase 0: Starting nodes (exclude parameters.json which appears later)
  const startingNodes = nodes.filter(n => !incomingMap[n.id] && n.id !== 'json');
  let currentTime = 0;
  
  // Track that json should be excluded initially
  const delayedNodes = new Set(['json']);
  
  if (startingNodes.length > 0) {
    phases.push({
      type: 'nodes',
      items: startingNodes,
      startTime: currentTime
    });
    
    startingNodes.forEach(node => {
      processedNodes.add(node.id);
      nodeTimings[node.id] = {
        delay: currentTime,
        duration: NODE_APPEAR_TIME / 1000,
        fromNode: null
      };
    });
    
    currentTime += NODE_APPEAR_TIME + PHASE_GAP;
  }
  
  // Build subsequent phases
  let safety = 0;
  while (processedNodes.size < nodes.length && safety < 20) {
    safety++;
    
    // Find connections from processed nodes
    const availableConnections = connections.filter((conn, idx) => 
      processedNodes.has(conn.from) && !processedConnections.has(idx)
    );
    
    if (availableConnections.length > 0) {
      // Add connection phase
      phases.push({
        type: 'connections',
        items: availableConnections,
        startTime: currentTime
      });
      
      availableConnections.forEach((conn, i) => {
        const idx = connections.indexOf(conn);
        processedConnections.add(idx);
        connectionTimings[idx] = {
          delay: currentTime,
          duration: LINE_DRAW_TIME / 1000,
          to: conn.to,
          from: conn.from
        };
      });
      
      currentTime += LINE_DRAW_TIME + PHASE_GAP;
      
      // Find nodes that can now appear (all incoming connections processed)
      const readyNodes = nodes.filter(node => {
        if (processedNodes.has(node.id)) return false;
        if (delayedNodes.has(node.id)) return false; // Skip delayed nodes
        const incoming = incomingMap[node.id] || [];
        return incoming.every(conn => {
          const idx = connections.indexOf(conn);
          return processedConnections.has(idx);
        });
      });
      
      if (readyNodes.length > 0) {
        // Add node phase
        phases.push({
          type: 'nodes',
          items: readyNodes,
          startTime: currentTime
        });
        
        readyNodes.forEach(node => {
          processedNodes.add(node.id);
          // Find primary incoming connection for transform origin
          const incoming = incomingMap[node.id] || [];
          const primaryConn = incoming[0];
          const fromNode = primaryConn ? nodes.find(n => n.id === primaryConn.from) : null;
          
          nodeTimings[node.id] = {
            delay: currentTime - NODE_APPEAR_TIME * 0.2, // Start slightly before line completes
            duration: NODE_APPEAR_TIME / 1000,
            fromNode: fromNode
          };
        });
        
        // Special case: Add parameters.json when Extract Headers appears
        if (readyNodes.some(n => n.id === 'extract')) {
          const jsonNode = nodes.find(n => n.id === 'json');
          if (jsonNode && !processedNodes.has('json')) {
            processedNodes.add('json');
            delayedNodes.delete('json'); // Remove from delayed set
            nodeTimings['json'] = {
              delay: currentTime + NODE_APPEAR_TIME * 0.5, // Appear slightly after extract
              duration: NODE_APPEAR_TIME / 1000,
              fromNode: null
            };
          }
        }
        
        currentTime += NODE_APPEAR_TIME + PHASE_GAP;
      }
    } else {
      break;
    }
  }
  
  // Synchronize connections to same target
  const targetGroups = {};
  connections.forEach((conn, idx) => {
    if (!targetGroups[conn.to]) targetGroups[conn.to] = [];
    targetGroups[conn.to].push(idx);
  });
  
  // Adjust timings for synchronized arrival
  Object.keys(targetGroups).forEach(targetId => {
    const connIndices = targetGroups[targetId];
    if (connIndices.length > 1) {
      // Find the latest start time
      let latestStart = 0;
      connIndices.forEach(idx => {
        if (connectionTimings[idx]) {
          latestStart = Math.max(latestStart, connectionTimings[idx].delay);
        }
      });
      
      // All connections to same target start at the latest time
      connIndices.forEach(idx => {
        if (connectionTimings[idx]) {
          connectionTimings[idx].delay = latestStart;
        }
      });
    }
  });
  
  return (
    <Container ref={containerRef}>
      {/* Render web-like connections */}
      <SVGContainer viewBox="0 0 100 100" preserveAspectRatio="none">
        {connections.map((conn, idx) => {
          const fromNode = nodes.find(n => n.id === conn.from);
          const toNode = nodes.find(n => n.id === conn.to);
          if (!fromNode || !toNode) return null;
          
          const path = conn.path || createPath(fromNode, toNode, true);
          const pathLength = getPathLength(path);
          
          // Get pre-calculated timing for this connection
          const timing = connectionTimings[idx] || { delay: 0, duration: 0.6 };
          
          // Calculate arrow position - adjust based on line direction
          let arrowPoints = null;
          const dx = toNode.x - fromNode.x;
          const dy = toNode.y - fromNode.y;
          
          // Detect line type and adjust position
          const isHorizontal = Math.abs(dy) < 5;
          const isVertical = Math.abs(dx) < 5;
          
          // Calculate actual midpoint on the bezier curve
          let midPoint;
          if (isHorizontal) {
            // Horizontal curve control points
            const p0 = { x: fromNode.x, y: fromNode.y };
            const p1 = { x: fromNode.x + dx * 0.5, y: fromNode.y };
            const p2 = { x: toNode.x - dx * 0.5, y: toNode.y };
            const p3 = { x: toNode.x, y: toNode.y };
            midPoint = getCubicBezierPoint(p0, p1, p2, p3, 0.5);
          } else if (isVertical) {
            // Vertical curve control points
            const p0 = { x: fromNode.x, y: fromNode.y };
            const p1 = { x: fromNode.x, y: fromNode.y + dy * 0.3 };
            const p2 = { x: toNode.x, y: toNode.y - dy * 0.3 };
            const p3 = { x: toNode.x, y: toNode.y };
            midPoint = getCubicBezierPoint(p0, p1, p2, p3, 0.5);
          } else {
            // Diagonal curve control points
            const p0 = { x: fromNode.x, y: fromNode.y };
            const p1 = { x: fromNode.x + dx * 0.25, y: fromNode.y };
            const p2 = { x: toNode.x - dx * 0.25, y: toNode.y };
            const p3 = { x: toNode.x, y: toNode.y };
            midPoint = getCubicBezierPoint(p0, p1, p2, p3, 0.5);
          }
          
          const midX = midPoint.x;
          const midY = midPoint.y;
          const angle = Math.atan2(dy, dx);
          
          // Create wider, more visible arrows
          const arrowLength = 2.2;  // Longer arrow
          const arrowWidth = 1.8;   // Wider arrow
          
          // Arrow points forward along the line
          const tipX = midX + arrowLength * 0.5 * Math.cos(angle);
          const tipY = midY + arrowLength * 0.5 * Math.sin(angle);
          
          // Base points perpendicular to line direction
          const perpAngle = angle + Math.PI / 2;
          const baseX = midX - arrowLength * 0.5 * Math.cos(angle);
          const baseY = midY - arrowLength * 0.5 * Math.sin(angle);
          
          const base1X = baseX + arrowWidth * 0.5 * Math.cos(perpAngle);
          const base1Y = baseY + arrowWidth * 0.5 * Math.sin(perpAngle);
          const base2X = baseX - arrowWidth * 0.5 * Math.cos(perpAngle);
          const base2Y = baseY - arrowWidth * 0.5 * Math.sin(perpAngle);
          
          arrowPoints = `${tipX},${tipY} ${base1X},${base1Y} ${base2X},${base2Y}`;
          
          return (
            <g key={idx}>
              <WebLine
                d={path}
                length={pathLength}
                visible={visible}
                delay={timing.delay}
                duration={timing.duration}
              />
              <Arrow
                points={arrowPoints}
                visible={visible}
                delay={timing.delay}
                duration={timing.duration}
              />
            </g>
          );
        })}
      </SVGContainer>
      
      {/* Render nodes */}
      {nodes.map((node, idx) => {
        const position = getPosition(node.x, node.y);
        
        const isEndpoint = idx === 0 || idx === nodes.length - 1;
        const nodeTiming = nodeTimings[node.id];
        
        // Calculate transform origin based on incoming connection
        let transformOrigin = 'center';
        if (nodeTiming && nodeTiming.fromNode) {
          const dx = node.x - nodeTiming.fromNode.x;
          const dy = node.y - nodeTiming.fromNode.y;
          
          // Determine which edge the line connects to
          if (Math.abs(dx) > Math.abs(dy)) {
            // Horizontal connection
            transformOrigin = dx > 0 ? '0% 50%' : '100% 50%'; // Left or right edge
          } else {
            // Vertical connection
            transformOrigin = dy > 0 ? '50% 0%' : '50% 100%'; // Top or bottom edge
          }
        }
        
        // Use timing from incoming connection or default
        const delay = nodeTiming ? nodeTiming.delay : 0;
        const duration = nodeTiming ? nodeTiming.duration : 0.15;
        
        return (
          <NodeElement
            key={node.id}
            style={position}
            visible={visible}
            delay={delay}
            duration={duration}
            isEndpoint={isEndpoint}
            transformOrigin={transformOrigin}
          >
            <NodeLabel isEndpoint={isEndpoint}>{node.label}</NodeLabel>
            {node.sublabel && <NodeSublabel>{node.sublabel}</NodeSublabel>}
          </NodeElement>
        );
      })}
    </Container>
  );
};

export default FlowDiagram;